import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, refreshToken, setBranch, setToken } from '@/lib/api'
import { configureLocale } from '@/lib/format'
import type { Me, PlatformMe } from '@/api/types'

type AnyMe = Me & { is_platform_admin?: boolean; tenant: Me['tenant'] | null }

interface AuthState {
  /** Hospital staff account (null for platform administrators). */
  me: Me | null
  /** PerfexData platform administrator (product console). */
  platform: PlatformMe | null
  status: 'loading' | 'authenticated' | 'anonymous'
  login: (email: string, password: string) => Promise<Me | PlatformMe>
  logout: () => Promise<void>
  reload: () => Promise<void>
  branchId: string | null
  selectBranch: (id: string) => void
}

const AuthContext = createContext<AuthState | null>(null)

function pickBranch(me: Me): string | null {
  let saved: string | null = null
  try {
    saved = localStorage.getItem('lh:branch')
  } catch {
    /* ignore */
  }
  if (saved && me.branches.some((b) => b.id === saved)) return saved
  return me.branches.find((b) => b.is_main)?.id ?? me.branches[0]?.id ?? null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [platform, setPlatform] = useState<PlatformMe | null>(null)
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [branchId, setBranchId] = useState<string | null>(null)
  const qc = useQueryClient()

  const applyMe = useCallback((raw: AnyMe): Me | PlatformMe => {
    if (raw.is_platform_admin && !raw.tenant) {
      const p: PlatformMe = { id: raw.id, email: raw.email, full_name: raw.full_name, must_change_password: raw.must_change_password, is_platform_admin: true }
      setMe(null)
      setPlatform(p)
      setStatus('authenticated')
      return p
    }
    const m = raw as Me
    setPlatform(null)
    configureLocale({ timeZone: m.tenant.timezone, currency: m.tenant.currency, showBs: m.tenant.show_bs_dates })
    const b = pickBranch(m)
    setBranch(b)
    setBranchId(b)
    setMe(m)
    setStatus('authenticated')
    return m
  }, [])

  const reload = useCallback(async () => {
    const m = await api.get<AnyMe>('/auth/me')
    applyMe(m)
  }, [applyMe])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = await refreshToken('staff')
      if (cancelled) return
      if (!token) {
        setStatus('anonymous')
        return
      }
      try {
        applyMe(await api.get<AnyMe>('/auth/me'))
      } catch {
        setStatus('anonymous')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applyMe])

  const login = useCallback(
    async (email: string, password: string) => {
      // Never carry the previous session's branch into a new sign-in: it may be one this user is not assigned
      // to (the server would refuse every request). applyMe picks one of this user's branches.
      setBranch(null)
      setBranchId(null)
      const res = await api.post<{ access_token: string }>('/auth/login', { email, password }, 'public')
      setToken(res.access_token)
      return applyMe(await api.get<AnyMe>('/auth/me'))
    },
    [applyMe],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      /* session may already be gone */
    }
    setToken(null)
    setBranch(null)
    setBranchId(null)
    setMe(null)
    setPlatform(null)
    setStatus('anonymous')
    qc.clear()
  }, [qc])

  const selectBranch = useCallback(
    (id: string) => {
      setBranch(id)
      setBranchId(id)
      try {
        localStorage.setItem('lh:branch', id)
      } catch {
        /* ignore */
      }
      // Drop every cached answer (not just mark it stale): the previous branch's data must never stay on screen,
      // e.g. while a request for a record of that branch is refused in the new one.
      qc.resetQueries()
    },
    [qc],
  )

  const value = useMemo(
    () => ({ me, platform, status, login, logout, reload, branchId, selectBranch }),
    [me, platform, status, login, logout, reload, branchId, selectBranch],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** Permission helpers. The backend enforces every rule; these only shape the UI. */
export function usePerms() {
  const { me } = useAuth()
  return useMemo(() => {
    const perms = new Set(me?.permissions ?? [])
    const mods = new Set(me?.enabled_modules ?? [])
    return {
      can: (...p: string[]) => p.every((x) => perms.has(x)),
      canAny: (...p: string[]) => p.some((x) => perms.has(x)),
      hasModule: (m: string) => mods.has(m),
      isClinician: !!me?.is_clinician,
    }
  }, [me])
}
